import React, { useEffect, useState } from "react";
import { MoneyLabel } from "../general/MoneyLabel.tsx";
import { SecondaryButton } from "../general/SecondaryButton.tsx";
import {
    ArrowUpIcon,
    ChevronDoubleLeftIcon,
    ChevronDoubleRightIcon,
    ChevronLeftIcon,
    ChevronRightIcon
} from "@heroicons/react/24/outline/index";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid/index";
import { DEFAULT_LOCALE } from "../../types/constants.ts";
import { cn } from "../../lib/utils.ts";

interface TableCellSpec<TableRowDataT> {
    row: TableRowDataT
    columnSpec: TableColumnSpec<TableRowDataT>
}

function getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
}

function TableCell<TableRowDataT>({ row, columnSpec }: TableCellSpec<TableRowDataT>) {
    if (columnSpec.customRender)
        return columnSpec.customRender(row)

    if (columnSpec.renderingPreset === RenderingPresetType.Money) {
        const value = getNestedValue(row, columnSpec.field);
        if (value == null || !isFinite(value)) {
            return <span></span>;
        }
        return (
            <>
                <MoneyLabel value={value / 100} />
            </>
        )
    }

    if (columnSpec.renderingPreset === RenderingPresetType.Date) {
        let dateM = getNestedValue(row, columnSpec.field);

        if (!dateM) {
            return <span>-</span>;
        }

        if (typeof dateM == 'string') {
            dateM = new Date(dateM);
        }

        if (!(dateM instanceof Date) || isNaN(dateM.getTime())) {
            return <span>-</span>;
        }

        return (
            <>
                <span>
                    {`${dateM.getDate().toString().padStart(2, '0')}-${(dateM.getMonth() + 1).toString().padStart(2, '0')}-${dateM.getFullYear()}`}
                </span>
            </>
        )
    }

    if (columnSpec.renderingPreset === RenderingPresetType.DateTime) {
        let dateM = getNestedValue(row, columnSpec.field);

        if (!dateM) {
            return <span>-</span>;
        }

        if (typeof dateM == 'string') {
            dateM = new Date(dateM);
        }

        if (!(dateM instanceof Date) || isNaN(dateM.getTime())) {
            return <span>-</span>;
        }

        return (
            <>
                <span>{dateM.toLocaleString("nl-NL", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "numeric",
                    minute: "numeric"
                })}</span>
            </>
        )
    }

    if (columnSpec.renderingPreset === RenderingPresetType.MoneyColorAid) {
        const value = getNestedValue(row, columnSpec.field);
        if (value == null || !isFinite(value)) {
            return <span></span>;
        }
        return (
            <>
                <MoneyLabel value={value / 100} colorAid={true} />
            </>
        )
    }

    let styles = ""

    if (columnSpec.style?.nowrap)
        styles += " whitespace-nowrap"

    return (
        <span className={styles}>
            {getNestedValue(row, columnSpec.field)}
        </span>
    )
}

interface RowDropzoneSpec<TableRowDataT> {
    onDropFile: (row: TableRowDataT, file: File) => void
    hint: string
    disabled?: (row: TableRowDataT) => boolean
}

interface TableRowProps<TableRowDataT> {
    columnSpecs: Array<TableColumnSpec<TableRowDataT>>
    row: TableRowDataT,
    onClick: (row: TableRowDataT) => void
    allowFullRowClick?: boolean
    rowDropzone?: RowDropzoneSpec<TableRowDataT>
}

function TableRowInner<TableRowDataT extends IdentifiableRecord>({ columnSpecs, onClick, row, allowFullRowClick = false, rowDropzone }: TableRowProps<TableRowDataT>) {
    const [isFileDragOver, setIsFileDragOver] = useState(false)

    function handleCellClick(e: React.MouseEvent) {
        if (!allowFullRowClick) {
            e.stopPropagation();
        }
    }

    const isDropEnabled = !!rowDropzone && !(rowDropzone.disabled?.(row))

    function isFileDrag(e: React.DragEvent) {
        return Array.from(e.dataTransfer?.types ?? []).includes('Files')
    }

    const dropHandlers = isDropEnabled ? {
        onDragOver: (e: React.DragEvent) => {
            if (!isFileDrag(e)) return
            e.preventDefault()
            e.dataTransfer.dropEffect = 'copy'
            setIsFileDragOver(true)
        },
        onDragLeave: (e: React.DragEvent) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setIsFileDragOver(false)
        },
        onDrop: (e: React.DragEvent) => {
            if (!isFileDrag(e)) return
            e.preventDefault()
            setIsFileDragOver(false)
            const file = e.dataTransfer.files?.[0]
            if (file) rowDropzone!.onDropFile(row, file)
        },
    } : {}

    return (
        <>
            <tr
                key={row?.id}
                data-item-id={row?.id}
                {...dropHandlers}
                className={"group/row w-full transition-colors hover:bg-gray-50 " + (onClick ? "cursor-pointer" : '')}
                onClick={(e) => onClick && onClick(row)}
            >
                {columnSpecs.map((columnSpec: TableColumnSpec<TableRowDataT>, idx) => (
                    <td
                        key={idx}
                        className={'py-3.5 px-4 border-b border-gray-100' + (columnSpec.style?.align === 'right' ? ' text-right' : columnSpec.style?.align === 'center' ? ' text-center' : '') + ` w-[${getColumnSize(columnSpecs, columnSpec) + '%'}]`}
                        style={{
                            width: getColumnSize(columnSpecs, columnSpec) + '%',
                            ...(isFileDragOver ? {
                                backgroundColor: 'color-mix(in srgb, var(--primary) 8%, transparent)',
                                borderTop: '2px dashed var(--primary)',
                                borderBottom: '2px dashed var(--primary)',
                                ...(idx === 0 ? {
                                    borderLeft: '2px dashed var(--primary)',
                                    borderTopLeftRadius: '8px',
                                    borderBottomLeftRadius: '8px',
                                } : {}),
                                ...(idx === columnSpecs.length - 1 ? {
                                    borderRight: '2px dashed var(--primary)',
                                    borderTopRightRadius: '8px',
                                    borderBottomRightRadius: '8px',
                                } : {}),
                            } : {})
                        }}
                    >
                        <div onClick={handleCellClick} className={(allowFullRowClick ? "cursor-pointer" : "cursor-auto")}>
                            <TableCell<TableRowDataT> row={row} columnSpec={columnSpec} />
                        </div>
                    </td>
                ))}
            </tr>
        </>
    )
}

const TableRow = React.memo(TableRowInner) as typeof TableRowInner;

const SKELETON_BAR_WIDTHS = ['66%', '50%', '75%', '58%', '42%'];

function SkeletonRow<TableRowDataT>({ columns, rowIndex }: { columns: Array<TableColumnSpec<TableRowDataT>>, rowIndex: number }) {
    return (
        <tr className="w-full">
            {columns.map((col, i) => {
                const isRight = col.style?.align === 'right';
                const kind = col.style?.skeleton ?? 'bar';
                return (
                    <td
                        key={i}
                        className={'py-3.5 px-4 border-b border-gray-100' + (isRight ? ' text-right' : '')}
                        style={{ width: getColumnSize(columns, col) + '%' }}
                    >
                        {kind === 'none' ? null
                            : kind === 'lead' ? (
                                <div className="flex items-center gap-3">
                                    <div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-gray-100" />
                                    <div className="min-w-0 space-y-1.5">
                                        <div className="h-3.5 w-32 animate-pulse rounded bg-gray-100" />
                                        <div className="h-3 w-20 animate-pulse rounded bg-gray-100" />
                                    </div>
                                </div>
                            ) : (
                                <div
                                    className={'h-4 animate-pulse rounded bg-gray-100' + (isRight ? ' inline-block' : '')}
                                    style={{ width: isRight ? '3.5rem' : SKELETON_BAR_WIDTHS[(rowIndex + i) % SKELETON_BAR_WIDTHS.length] }}
                                />
                            )}
                    </td>
                );
            })}
        </tr>
    );
}

export enum RenderingPresetType {
    Money,
    MoneyColorAid,
    Date,
    DateTime
}


export type SortableValue = string | number | null | undefined

const SORT_COMPARE_OPTIONS: Intl.CollatorOptions = { numeric: true, sensitivity: 'base' }

const EMPTY_SORT_VALUE_ORDER = 1
const PRESENT_SORT_VALUE_ORDER = -1
const EQUAL_SORT_ORDER = 0

const ASCENDING = 1
const DESCENDING = -1

const FIRST_PAGE = 1

function isEmptySortValue(value: SortableValue): boolean {
    return value === null || value === undefined || value === ''
}

function compareEmptiness(a: SortableValue, b: SortableValue): number {
    const aIsEmpty = isEmptySortValue(a)
    const bIsEmpty = isEmptySortValue(b)

    if (aIsEmpty === bIsEmpty)
        return EQUAL_SORT_ORDER

    return aIsEmpty ? EMPTY_SORT_VALUE_ORDER : PRESENT_SORT_VALUE_ORDER
}

function comparePresentSortValues(a: SortableValue, b: SortableValue): number {
    if (typeof a === 'number' && typeof b === 'number')
        return a - b

    return String(a).localeCompare(String(b), DEFAULT_LOCALE, SORT_COMPARE_OPTIONS)
}

export interface TableColumnSpec<TableDataT> {
    name: string | React.ReactNode,
    field: string,
    style?: {
        nowrap?: boolean
        widthMultiplier?: number
        align?: 'left' | 'right' | 'center'
        skeleton?: 'lead' | 'bar' | 'none'
    }
    customRender?: (row: TableDataT) => any
    renderingPreset?: RenderingPresetType
    disableSorting?: boolean
    sortValue?: (row: TableDataT) => SortableValue
}

export interface PaginationState<TableDataT> {
    currentPage: number,
    pageSize: number
    selectedItemsInMemory?: Array<TableDataT>
}

export interface SearchState {
    currentQuery: string
}

export interface SortingState {
    orderBy: string,
    orderDescending: boolean
}

export const NO_SORTING_STATE: SortingState = { orderBy: '', orderDescending: false }

export interface TableScrollConfig {
    maxWidth?: number | string | boolean
    maxHeight?: number | string
}

function EmptyBoxIcon() {
    return (
        <svg
            width="64"
            height="41"
            viewBox="0 0 64 41"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
        >
            <g transform="translate(0 1)" fill="none" fillRule="evenodd">
                <ellipse cx="32" cy="33" rx="32" ry="7" fill="#F5F5F5" />
                <g fillRule="nonzero" stroke="#D9D9D9">
                    <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z" />
                    <path
                        d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z"
                        fill="#FAFAFA"
                    />
                </g>
            </g>
        </svg>
    );
}

function EmptyTableBody({
    colSpan,
    message,
}: {
    colSpan: number;
    message: string;
}) {
    return (
        <tr>
            <td colSpan={colSpan} className="px-6 py-16">
                <div className="flex flex-col items-center justify-center gap-2 text-center">
                    <EmptyBoxIcon />
                    <p className="mt-1 text-[14px] text-gray-400">{message}</p>
                </div>
            </td>
        </tr>
    );
}

export interface TableHeaderSpec<TableDataT> {
    title?: string
    columns: Array<TableColumnSpec<TableDataT>>
    isLoading?: boolean
    skeletonRows?: number
    pagination?: {
        inMemory?: boolean,
        paginationState: [
            paginationState: PaginationState<TableDataT>,
            setPaginationState: (paginationState: any) => void
        ]
        defaultPageSize: number,
        totalPages: number,
    }
    sorting?: {
        sortingState: [
            sortingState: SortingState,
            setSortingState: (sortingState: any) => void
        ]
        inMemory?: boolean
    }
    onEmptyRender?: () => any,
    emptyRowMessage?: string,
    footer?: React.ReactNode,
    search?: {
        searchFn: (row: TableDataT, searchState: SearchState) => boolean
        filters?: React.ReactNode
        actions?: React.ReactNode
        className?: string
    }
    onRowClick?: (row: TableDataT) => void
    rowDropzone?: RowDropzoneSpec<TableDataT>
    options?: {
        disableTopPaginationWidget?: boolean
        disableBottomPaginationWidget?: boolean
        hideTableHeader?: boolean
        allowFullRowClick?: boolean
    }
    scroll?: TableScrollConfig
    onQueryUpdate?: (pagination: PaginationState<TableDataT> | null, sorting: SortingState | null) => void
}

interface IdentifiableRecord {
    id: string
}

interface SortingIndicatorProps {
    isActive: boolean
    isDescending: boolean
    onUpdate: (isAscending: boolean) => void
}

function SortingIndicator({ isDescending, isActive, onUpdate }: SortingIndicatorProps) {
    let style = " cursor-pointer hover:bg-gray-100 rounded-full p-1 transition"

    if (isDescending) {
        style += " rotate-180 "
    }

    if (!isActive) {
        style += " opacity-0 hover:opacity-100"
    }

    return (
        <div className={style} onClick={() => {

            if (isActive)
                onUpdate(!isDescending)
            else
                onUpdate(false)
        }}>
            <ArrowUpIcon className={"size-4"} />
        </div>
    )
}

function getColumnSize<TableDataT>(columns: Array<TableColumnSpec<TableDataT>>, column: TableColumnSpec<TableDataT>) {

    let fractions = 0

    for (const columnSpec of columns) {
        if (columnSpec?.style?.widthMultiplier)
            fractions += columnSpec.style.widthMultiplier
        else
            fractions += 1
    }

    const baseColumnWidth = 100 / fractions

    const currentColumnWidthMultiplier = column.style?.widthMultiplier || 1

    return baseColumnWidth * currentColumnWidthMultiplier
}

function getScrollStyles(scrollConfig?: TableScrollConfig) {

    function convertToCssUnit(value: number | string): string {
        if (typeof value === 'number') {
            return `${value}px`
        }
        return value
    }

    const styles: React.CSSProperties = {}

    if (scrollConfig) {
        if (scrollConfig.maxWidth === true) {
            styles.overflowX = 'auto'
        } else if (scrollConfig.maxWidth) {
            styles.overflowX = 'auto'
            const normalizedMaxWidth = convertToCssUnit(scrollConfig.maxWidth)
            styles.maxWidth = normalizedMaxWidth
        }

        if (scrollConfig.maxHeight) {
            styles.overflowY = 'auto'
            const normalizedMaxHeight = convertToCssUnit(scrollConfig.maxHeight)
            styles.maxHeight = normalizedMaxHeight
        }
    }

    return styles
}

function getDefaultScrollConfig(): TableScrollConfig {
    return {
        maxWidth: '100%',
    }
}


export function createTable<TableDataT extends IdentifiableRecord>(tableData: Array<TableDataT>, headers: TableHeaderSpec<TableDataT>) {


    function TableComponent({ data: dataProp }: { data?: Array<TableDataT> } = {}) {
        const activeData = dataProp ?? tableData;
        const [searchState, setSearchState] = useState<SearchState>({
            currentQuery: ''
        })

        function getTotalPages() {
            return Math.ceil(getFilteredTableData().length / headers?.pagination?.defaultPageSize)
        }

        const [paginationState, setPaginationState] = headers?.pagination?.paginationState || [null, null]

        const [sortingState, setSortingState] = headers?.sorting?.sortingState || [null, null]

        const isPaginationEnabled = !!headers?.pagination
        const isSortingEnabled = 'sorting' in headers

        if (headers?.pagination && paginationState === null) {
            setPaginationState({
                currentPage: FIRST_PAGE
            })
        }


        function canIncrementPage() {
            return paginationState?.currentPage < getTotalPages()
        }

        function canDecrementPage() {
            return paginationState?.currentPage > 1
        }

        function inMemoryPaginationUpdater(currentPage: number, pageSize: number) {
            const startOffset = (currentPage - 1) * pageSize
            const endOffset = currentPage * pageSize
            setPaginationState(
                (prev: PaginationState<TableDataT>) => {
                    return {
                        ...prev,
                        selectedItemsInMemory: getFilteredTableData().slice(startOffset, endOffset),
                    }
                }
            )
        }

        function handlePageUpdate(change: number) {
            if (change === 0) return

            if (canIncrementPage() && change > 0) {
                const currentPage = paginationState.currentPage + change

                setPaginationState((prev) => {
                    const newState = { ...prev, currentPage: currentPage }
                    if (!headers.pagination.inMemory)
                        headers.onQueryUpdate(newState, sortingState)
                    return newState
                })
                inMemoryPaginationUpdater(currentPage, paginationState.pageSize)
            }

            if (canDecrementPage() && change < 0) {
                const currentPage = paginationState.currentPage + change

                setPaginationState((prev) => {
                    const newState = { ...prev, currentPage: prev.currentPage + change }

                    if (!headers.pagination.inMemory)
                        headers.onQueryUpdate(newState, sortingState)

                    return newState
                })
                inMemoryPaginationUpdater(currentPage, paginationState.pageSize)
            }
        }

        function handleSorting(field: string, orderDescending: boolean) {
            setSortingState((prev) => {
                const newState = { ...prev, orderBy: field, orderDescending: orderDescending }
                if (!headers.sorting.inMemory)
                    headers.onQueryUpdate(paginationState, newState)
                return newState
            })

            if (headers?.pagination?.inMemory && paginationState)
                inMemoryPaginationUpdater(paginationState.currentPage, paginationState.pageSize)
        }


        useEffect(() => {
            if (headers?.pagination?.inMemory) {
                setPaginationState({
                    currentPage: FIRST_PAGE,
                    pageSize: headers.pagination.defaultPageSize
                })
            }

            if (headers?.pagination?.inMemory) {
                inMemoryPaginationUpdater(FIRST_PAGE, headers.pagination.defaultPageSize)
            }


        }, []);

        useEffect(() => {
            if (!(headers?.pagination?.inMemory && paginationState?.currentPage))
                return

            const totalPages = getTotalPages()
            const currentPage = paginationState.currentPage > totalPages ? FIRST_PAGE : paginationState.currentPage

            if (currentPage !== paginationState.currentPage)
                setPaginationState((prev: PaginationState<TableDataT>) => ({ ...prev, currentPage }))

            inMemoryPaginationUpdater(currentPage, headers?.pagination?.defaultPageSize)
        }, [activeData, searchState, sortingState]);

        function getSortedTableData() {
            return sortData(activeData)
        }

        function sortData(data: Array<TableDataT>): Array<TableDataT> {
            if (!sortingState || !headers?.sorting?.inMemory || !data)
                return data

            const columnToSortOn = headers.columns.find((column) => column.field === sortingState.orderBy)
            if (!columnToSortOn || columnToSortOn.disableSorting)
                return data

            const readSortValue = (row: TableDataT): SortableValue =>
                columnToSortOn.sortValue
                    ? columnToSortOn.sortValue(row)
                    : getNestedValue(row, columnToSortOn.field)

            const direction = sortingState.orderDescending ? DESCENDING : ASCENDING

            return [...data].sort((rowA, rowB) => {
                const a = readSortValue(rowA)
                const b = readSortValue(rowB)

                const emptiness = compareEmptiness(a, b)
                if (emptiness !== EQUAL_SORT_ORDER)
                    return emptiness

                return direction * comparePresentSortValues(a, b)
            })
        }

        function getFilteredTableData() {
            if (headers?.search) {
                return getSortedTableData()?.filter((row) => headers.search.searchFn(row, searchState))
            }

            return getSortedTableData();
        }

        function getProcessedTableData() {
            return (headers?.pagination?.inMemory ? paginationState?.selectedItemsInMemory : getFilteredTableData())
        }

        if (!headers?.isLoading && !getSortedTableData().length && headers?.onEmptyRender !== undefined) {
            return <>{headers.onEmptyRender()}</>
        }

        const scrollConfig = headers?.scroll ? { ...getDefaultScrollConfig(), ...headers.scroll } : getDefaultScrollConfig()
        const scrollStyles = getScrollStyles(scrollConfig)

        return (
            <>
                {!headers?.options?.disableTopPaginationWidget &&
                    <div className="flex items-center justify-between gap-2 py-2 w-full">
                        <h2>{headers?.title}</h2>
                        {(isPaginationEnabled) &&
                            <div className="flex items-center gap-2 py-2">
                                <SecondaryButton
                                    ariaLabel={"First Page"}
                                    title={''}
                                    icon={ChevronDoubleLeftIcon}
                                    disabled={!canDecrementPage()}
                                    onClick={() => {
                                        handlePageUpdate(-(paginationState.currentPage - 1))
                                    }}
                                />
                                <SecondaryButton
                                    ariaLabel={"Previous Page"}
                                    title={''}
                                    icon={ChevronLeftIcon}
                                    disabled={!canDecrementPage()}
                                    onClick={() => {
                                        handlePageUpdate(-1)
                                    }}
                                />

                                <span>{paginationState?.currentPage} of {getTotalPages()}</span>
                                <SecondaryButton
                                    ariaLabel={"Next Page"}
                                    title={''}
                                    icon={ChevronRightIcon}
                                    disabled={!canIncrementPage()}
                                    onClick={() => {
                                        handlePageUpdate(+1)
                                    }}
                                />
                                <SecondaryButton
                                    ariaLabel={"Last Page"}
                                    title={''}
                                    icon={ChevronDoubleRightIcon}
                                    disabled={!canIncrementPage()}
                                    onClick={() => {
                                        handlePageUpdate(getTotalPages() - paginationState.currentPage)
                                    }}
                                />
                            </div>
                        }
                    </div>}

                {headers?.search && <div className="flex items-center gap-3 mb-2">
                    <div
                        className={cn("grid grow grid-cols-1 border border-gray-200 rounded-md bg-white", headers.search.className)}>
                        <input
                            name="search"
                            key="search"
                            type="search"
                            placeholder="Zoeken"
                            aria-label="Zoeken"
                            value={searchState.currentQuery}
                            onChange={(e) => {
                                setSearchState({ currentQuery: e.target.value })
                                if (headers?.pagination?.inMemory && setPaginationState) {
                                    setPaginationState((prev: any) => prev ? { ...prev, currentPage: FIRST_PAGE } : prev)
                                }
                            }}
                            className="col-start-1 row-start-1 block w-full rounded-md bg-white py-1 pl-10 pr-3 text-base text-gray-900 outline-none placeholder:text-gray-400 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-white/40 sm:text-sm/6"
                        />
                        <MagnifyingGlassIcon
                            aria-hidden="true"
                            className="pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-gray-400"
                        />
                    </div>
                    {headers.search.filters && <div className="shrink-0 mr-auto">{headers.search.filters}</div>}
                    {headers.search.actions && <div className="shrink-0">{headers.search.actions}</div>}
                </div>}

                <div className="rounded-xl border border-gray-200 overflow-hidden" style={scrollStyles}>
                    <table
                        className="border-separate border-spacing-0 w-full text-sm text-left rtl:text-right text-gray-700 dark:text-gray-400">

                        {!headers.options?.hideTableHeader && <thead className="bg-white sticky top-0 z-10">
                            <tr className="text-sm font-semibold text-gray-500">
                                {headers.columns.map((header: TableColumnSpec<TableDataT>) => (
                                    <th key={header.field} className="px-4 py-3 text-nowrap bg-white border-b border-gray-200">
                                        <div className={"flex items-center gap-x-2 group" + (header.style?.align === 'right' ? ' w-full justify-end' : header.style?.align === 'center' ? ' w-full justify-center' : '')}
                                            style={header.style?.align ? undefined : { width: getColumnSize(headers.columns, header) + '%' }}>
                                            {header.name}
                                            {isSortingEnabled && (!header.disableSorting) && header.field &&
                                                <SortingIndicator isActive={sortingState?.orderBy === header.field}
                                                    isDescending={sortingState?.orderDescending}
                                                    onUpdate={(isAscending) => {
                                                        handleSorting(header.field, isAscending)
                                                    }} />
                                            }

                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>}

                        <tbody>

                            {headers?.isLoading
                                ? Array.from({ length: headers.skeletonRows ?? 8 }).map((_, r) => (
                                    <SkeletonRow<TableDataT> key={r} columns={headers.columns} rowIndex={r} />
                                ))
                                : getProcessedTableData()?.map((row: TableDataT) => (
                                    <TableRow<TableDataT> key={row.id} columnSpecs={headers.columns} row={row} onClick={headers?.onRowClick} allowFullRowClick={headers?.options?.allowFullRowClick} rowDropzone={headers.rowDropzone} />
                                ))}
                            {!headers?.isLoading && headers?.emptyRowMessage !== undefined && getSortedTableData()?.length === 0 && (
                                <EmptyTableBody colSpan={headers.columns.length} message={headers.emptyRowMessage} />
                            )}
                            {!headers?.isLoading && headers?.footer && (
                                <tr>
                                    <td colSpan={headers.columns.length} className="p-0">
                                        {headers.footer}
                                    </td>
                                </tr>
                            )}
                            {!headers?.isLoading && getProcessedTableData()?.length === 0 && getSortedTableData()?.length > 0 && (
                                <tr>
                                    <td colSpan={headers.columns.length} className="py-8 text-center text-gray-400 text-sm">
                                        Geen resultaten gevonden
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {!headers?.options?.disableBottomPaginationWidget &&
                    <div className="flex items-center justify-between gap-2 py-2 w-full">
                        <h2>{headers?.title}</h2>
                        {isPaginationEnabled &&
                            <div className="flex items-center gap-2 py-2">
                                <SecondaryButton
                                    ariaLabel={"First Page"}
                                    title={''}
                                    icon={ChevronDoubleLeftIcon}
                                    disabled={!canDecrementPage()}
                                    onClick={() => {
                                        handlePageUpdate(-(paginationState.currentPage - 1))
                                    }}
                                />
                                <SecondaryButton
                                    ariaLabel={"Previous Page"}
                                    title={''}
                                    icon={ChevronLeftIcon}
                                    disabled={!canDecrementPage()}
                                    onClick={() => {
                                        handlePageUpdate(-1)
                                    }}
                                />

                                <span>{paginationState?.currentPage} of {getTotalPages()}</span>
                                <SecondaryButton
                                    ariaLabel={"Next Page"}
                                    title={''}
                                    icon={ChevronRightIcon}
                                    disabled={!canIncrementPage()}
                                    onClick={() => {
                                        handlePageUpdate(+1)
                                    }}
                                />
                                <SecondaryButton
                                    ariaLabel={"Last Page"}
                                    title={''}
                                    icon={ChevronDoubleRightIcon}
                                    disabled={!canIncrementPage()}
                                    onClick={() => {
                                        handlePageUpdate(getTotalPages() - paginationState.currentPage)
                                    }}
                                />
                            </div>
                        }
                    </div>}
            </>
        )
    }

    return TableComponent
}
